import {
  ArrowLeft,
  ArrowRight,
  Diamond,
  ExternalLinkIcon,
  Maximize,
  PictureInPicture,
  RefreshCcw,
} from "lucide-react";
import { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GLOBAL_DEV_MODE,
  useIframePoolStore,
} from "@/components/Canvas/IframePool/iframePoolStore";
import {
  useCanvasPos,
  useCanvasStore,
  useHoveredNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { cn } from "@/lib/utils";
import { CursorType } from "@/store/globalSettingsSlice";
import { setSelectedElementKey } from "@/store/iframeSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { usePreviewGeneratorStore } from "@/store/zustand/previewGeneratorStore";
import { ACTION_TYPES } from "@/utils/ai/utils";
import {
  STORYBOARD_TYPES,
  STORYBOARD_TYPES_WITH_MINIMAL_HEADER,
} from "@/utils/constants";
import { TempoElement } from "@/utils/tempoElement";
import type { Storyboard as StoryboardType } from "@/utils/types";
import { useActiveCanvas } from "@/utils/utils";
import type {
  CanvasItemState,
  SimpleCanvasRef,
} from "../../SimpleCanvas/SimpleCanvas";
import { useCanvasContextMenu } from "../hooks/useCanvasContextMenu";
import { useFullScreenMode } from "../hooks/useFullScreenMode";
import { useStoryboardUrl } from "../hooks/useStoryboardUrl";
import useUpdateStoryboardName from "../hooks/useUpdateStoryboardName";
import { UNZOOM_MULTIPLIER_CSS_VAR } from "../utils/utils";
import { isNavTreeEmpty } from "./StaticIframe/utils/navTreeLookupUtils";
import StoryboardResizeMenu from "./StoryboardResizeMenu";
import { ToolbarIconButton } from "./ToolbarIconButton";
import { UrlCombobox } from "./UrlCombobox";

// Observed max height from DOM when zoomed out
export const CONTROLS_MAX_HEIGHT = 100;

interface StoryboardControlsOverlayProps {
  storyboard: StoryboardType;
  canvasItemState: CanvasItemState;
  index?: number;

  showDot: "red" | "yellow" | "none";
  dotTooltip: string;

  isLoading: boolean;

  simpleCanvasRef: React.RefObject<SimpleCanvasRef>;

  forceNotInteractable: boolean;
}

/**
 * Renders the control overlay above the storyboard with navigation, status, and actions.
 * Handles all user interactions and UI state for the storyboard controls.
 */
export const StoryboardControlsOverlay = memo(
  ({
    storyboard,
    canvasItemState,
    showDot,
    dotTooltip,
    isLoading,
    simpleCanvasRef,
    forceNotInteractable,
  }: StoryboardControlsOverlayProps) => {
    const DEV_MODE = GLOBAL_DEV_MODE || false;

    // Fullscreen functionality
    const { enterFullscreen, isInFullScreenMode } = useFullScreenMode();

    // Local UI state
    const [showStoryboardControls, setShowStoryboardControls] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState(
      storyboard.name || "Storyboard",
    );

    const cursorType = useSelector(
      (state: any) => state.globalSettings.cursorType,
    );
    const isDriveMode = cursorType === CursorType.Drive;

    // Store connections
    const dispatch = useDispatch();
    const canvasPos = useCanvasPos();
    const zoomPerc = canvasPos?.zoomPerc || 1;

    const aiActionType = useAiActionStore((state) => state.actionType);
    const activeCanvas = useActiveCanvas();

    // Get canvas component key to check if we're in a subcanvas
    const canvasComponentKey = useCanvasStore(
      (store) => store.canvasComponentKey,
    );

    // Canvas store data for display - use shallow comparison to prevent re-renders
    const navTreeForThisStoryboard = useCanvasStore(
      (store) => store.storyboardToNavTree[storyboard.id],
      (a, b) => a === b,
    );

    const storyboardUrl = useStoryboardUrl(storyboard);

    // Hooks
    const { setName } = useUpdateStoryboardName(activeCanvas);

    const isSpacePressed = useSelector(
      (state: any) => state.globalSettings.isSpacePressed,
    );

    const notInteractable =
      cursorType === CursorType.Pan || isSpacePressed || forceNotInteractable;

    // Computed values for UI
    const { storyboardIdHovered } = useHoveredNavTreeNode();
    const storyboardHovered = storyboardIdHovered === storyboard?.id;

    const storyboardSelected = canvasItemState.isSelected;
    const minimalHeader = STORYBOARD_TYPES_WITH_MINIMAL_HEADER.has(
      storyboard.type || "component",
    );

    const showSpecialNameStyle =
      ((storyboard.saved && storyboard.storyCodebaseId) ||
        storyboard.renderStatus === "placeholder") &&
      minimalHeader;

    const isEmptyNavTree = isNavTreeEmpty(navTreeForThisStoryboard);

    // Zoom calculations
    // If adjusting, also adjust the CONTROLS_MAX_HEIGHT constant based on the
    // max zoomed out height of the controls
    const zoomMultiplier = Math.min(2, (1 / zoomPerc) * 0.45);
    const zoomMultiplierCssVar = UNZOOM_MULTIPLIER_CSS_VAR(2, 0.45);
    const hideExtraControls =
      (storyboard.width || 400) / zoomMultiplier < 425 ||
      storyboard.renderStatus === "placeholder" ||
      (minimalHeader && !storyboardSelected);
    const forceShowOnlyText =
      24 * zoomMultiplier > (storyboard.width || 400) / 2;

    const refreshStoryboard = useIframePoolStore(
      (state) => state.refreshStoryboard,
    );

    // Control visibility
    const showControls =
      storyboardSelected || storyboardHovered || showStoryboardControls;

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================

    const handleNameClick = () => {
      if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
        return;
      }
      setIsEditingName(true);
    };

    const handleNameBlur = () => {
      setName(storyboard, editedName);
      setIsEditingName(false);
    };

    const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleNameBlur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setEditedName(storyboard.name || "Storyboard");
        setIsEditingName(false);
      }
    };

    const handleRefreshClick = async () => {
      refreshStoryboard(storyboard.id);
    };

    const handleExternalLinkClick = () => {
      window.open(storyboardUrl, "_blank")?.focus();
    };

    const handleMaximizeClick = () => {
      enterFullscreen(storyboard);
    };

    // Show maximize button for all storyboards when not in full screen mode
    const showMaximizeButton = !isInFullScreenMode(storyboard.id);

    const { handleContextMenu } = useCanvasContextMenu(storyboard?.id);

    // ============================================================================
    // RENDER COMPONENTS
    // ============================================================================

    const renderNavigationControls = () => (
      <>
        {storyboard.type === STORYBOARD_TYPES.APPLICATION && (
          <>
            <ToolbarIconButton
              icon={ArrowLeft}
              aria-label="Back"
              tooltipContent={
                isLoading ? "Waiting for storyboard to load..." : "Back"
              }
              disabled={isLoading}
              onClick={() => {
                if (isLoading) return;
                useIframePoolStore
                  .getState()
                  .sendMessageToApplicationStoryboard(
                    storyboard.id,
                    "executeFunction",
                    {
                      functionName: "goBack",
                      args: [],
                    },
                  );
              }}
            />
            <ToolbarIconButton
              icon={ArrowRight}
              aria-label="Forward"
              tooltipContent={
                isLoading ? "Waiting for storyboard to load..." : "Forward"
              }
              disabled={isLoading}
              onClick={() => {
                if (isLoading) return;
                useIframePoolStore
                  .getState()
                  .sendMessageToApplicationStoryboard(
                    storyboard.id,
                    "executeFunction",
                    {
                      functionName: "goForward",
                      args: [],
                    },
                  );
              }}
            />
          </>
        )}
        <ToolbarIconButton
          icon={RefreshCcw}
          aria-label="Refresh"
          tooltipContent="Refresh"
          onClick={handleRefreshClick}
        />
        {/* Only show camera button in subcanvases */}
        {canvasComponentKey && (
          <ToolbarIconButton
            icon={PictureInPicture}
            aria-label="Save Preview"
            tooltipContent={
              isLoading || !navTreeForThisStoryboard || isEmptyNavTree
                ? "Waiting for storyboard to load..."
                : "Save Preview"
            }
            disabled={
              isLoading ||
              !navTreeForThisStoryboard ||
              isEmptyNavTree ||
              isDriveMode
            }
            onClick={() => {
              if (!storyboard.storyCodebaseId) {
                return;
              }
              usePreviewGeneratorStore
                .getState()
                .queueElements(
                  [
                    useCodebaseStore.getState().treeElementLookup[
                      storyboard.storyCodebaseId
                    ],
                  ],
                  true,
                );
            }}
          />
        )}
      </>
    );

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    return (
      <div
        className="absolute left-0"
        style={{
          top: `calc(-((${
            showSpecialNameStyle ? 20 : 16
          } * ${zoomMultiplierCssVar}) + 16px))`, // Position above storyboard with gap
          pointerEvents: "none",
          height: `calc((${
            showSpecialNameStyle ? 20 : 16
          } * ${zoomMultiplierCssVar}) + 16px)`, // Set explicit height
          width: "100%",
        }}
      >
        {/** biome-ignore lint/a11y/noStaticElementInteractions: no aira better than bad aria */}
        <div
          className={cn(
            "flex absolute flex-row gap-0 select-none p-1 rounded-2xl",
            "text-icons-secondary items-center justify-between bg-background-tertiary border-background-tertiary",
            aiActionType === ACTION_TYPES.EDIT && "ai-outline",
            minimalHeader && "bg-transparent px-0 h-12",
            !notInteractable && "cursor-pointer",
          )}
          style={{
            width: `calc(100% / ${zoomMultiplierCssVar})`,
            transformOrigin: "bottom left",
            pointerEvents: notInteractable ? "none" : "initial",

            // If adjusting, also adjust the CONTROLS_MAX_HEIGHT constant based on the
            // max zoomed out height of the controls
            transform: `scale(${zoomMultiplierCssVar})`,
            bottom: `calc(16px * ${zoomMultiplierCssVar})`,
          }}
          onContextMenu={handleContextMenu}
          onMouseEnter={() => setShowStoryboardControls(true)}
          onMouseLeave={() => setShowStoryboardControls(false)}
        >
          {/* Status dot */}
          <div
            data-tooltip-id="tooltip"
            data-tooltip-place="top"
            data-tooltip-content={dotTooltip || ""}
            className={cn(
              "w-4 h-4 rounded-full shrink-0 mr-2",
              showDot === "red" && "bg-red-500",
              showDot === "yellow" && "bg-yellow-500",
              showDot === "none" && "hidden",
              storyboard?.type === STORYBOARD_TYPES.APPLICATION && "ml-4",
            )}
          />

          {/* Left controls section */}
          {!hideExtraControls && !showSpecialNameStyle
            ? renderNavigationControls()
            : null}

          {/* Center name section */}
          <div
            className={cn(
              "text-2xl text-center shrink flex-auto min-w-0 flex flex-row items-center justify-start gap-3 ml-1",
              minimalHeader && "gap-2",
            )}
          >
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                // biome-ignore lint/a11y/noAutofocus: is the only field associated with isEditingName
                autoFocus
              />
            ) : storyboard.type === STORYBOARD_TYPES.APPLICATION ? (
              <UrlCombobox
                storyboardId={storyboard.id}
                disabled={notInteractable}
              />
            ) : (
              // biome-ignore lint/a11y/noStaticElementInteractions: no aria better than bad aria
              // biome-ignore lint/a11y/useKeyWithClickEvents: todo add keyboard interaction?
              <div
                className={cn(
                  "text-ellipsis text-white whitespace-nowrap truncate bg-transparent text-secondary-n-500 rounded-lg flex items-center gap-2.5",
                  showSpecialNameStyle && "bg-secondary-n-1000 p-3 font-medium",
                  storyboardSelected &&
                    showSpecialNameStyle &&
                    "bg-primary-0 text-white",
                )}
                style={forceShowOnlyText ? { overflow: "visible" } : {}}
                onDoubleClick={handleNameClick}
                onClick={() => {
                  dispatch(
                    setSelectedElementKey(
                      TempoElement.forStoryboard(storyboard.id).getKey(),
                    ),
                  );
                }}
              >
                <Diamond
                  className={cn(
                    "size-5 min-w-5 text-primary-n-500 transition-colors",
                    storyboardSelected && "text-white",
                    !(
                      (storyboard.storyUniqueIdentifier ||
                        storyboard.renderStatus === "placeholder") &&
                      minimalHeader
                    ) && "hidden",
                  )}
                />
                {(() => {
                  let storyboardLabel = storyboard.name;

                  if (forceShowOnlyText) {
                    storyboardLabel =
                      (storyboardLabel?.substring(0, 1) || "") + "..";
                  }

                  if (DEV_MODE) {
                    storyboardLabel = storyboard.id;
                  }

                  return storyboardLabel;
                })()}
              </div>
            )}
            {/* If this is the minimal header, refresh left/right are shown to the right of the name */}
            {!hideExtraControls && showSpecialNameStyle
              ? renderNavigationControls()
              : null}
          </div>
          {!hideExtraControls && (
            <ToolbarIconButton
              data-testid="storyboard-preview-in-tab-button"
              icon={ExternalLinkIcon}
              aria-label="Preview in Tab"
              tooltipContent="Preview in Tab"
              disabled={!storyboardUrl}
              onClick={() => {
                if (!storyboardUrl) return;
                handleExternalLinkClick();
              }}
            />
          )}

          {/* Right controls section */}
          {!hideExtraControls && (
            <StoryboardResizeMenu
              storyboard={storyboard}
              canvas={activeCanvas}
              data-tooltip-id="tooltip"
              data-tooltip-place="top"
              data-tooltip-content="Change size"
              className={cn(
                "w-10 h-10 cursor-pointer p-1 select-none focus:outline-hidden shrink-0",
              )}
            />
          )}
          {!hideExtraControls && showMaximizeButton && (
            <ToolbarIconButton
              icon={Maximize}
              aria-label="Full screen"
              tooltipContent="Full screen"
              onClick={handleMaximizeClick}
            />
          )}
        </div>
      </div>
    );
  },
);

StoryboardControlsOverlay.displayName = "StoryboardControlsOverlay";
